/**
 * Flag sprite position map.
 *
 * Sprite: `packages/ds-shared/src/assets/flags.png` (384x360px, 16x15 grid,
 * 24x24px per tile). Positions are derived 1:1 from the canonical
 * `_flags.scss` reference distributed with the design system (CSS Flag
 * Sprite generator output) — keys mirror the `.flag-XX` class names from
 * that file. Most are ISO 3166-1 alpha-2 country codes; a handful are
 * BCP-47-style locale tags (en-US, pt-br) for variant flags.
 *
 * Coordinates are 0-indexed:
 *   col 0..15 -> x = -col * 24px
 *   row 0..14 -> y = -row * 24px
 *
 * Lookup is locale-aware via `getFlagPosition()` — given a BCP-47 tag like
 * 'en-US', the resolver tries direct match, lowercased match, region-only
 * fallback ('us'), and language-only fallback ('en') in that order.
 */

export interface FlagPosition {
  col: number;
  row: number;
}

export const FLAG_TILE_PX = 24;
export const FLAG_SPRITE_COLS = 16;
export const FLAG_SPRITE_ROWS = 15;

export const FLAG_POSITIONS: Record<string, FlagPosition> = {
  'ad': { col:  1, row:  0 },
  'af': { col:  3, row:  0 },
  'ag': { col:  4, row:  0 },
  'ai': { col:  5, row:  0 },
  'al': { col:  6, row:  0 },
  'am': { col:  7, row:  0 },
  'an': { col:  8, row:  0 },
  'ao': { col:  9, row:  0 },
  'ae': { col:  2, row:  0 },
  'ar': { col: 10, row:  0 },
  'as': { col: 11, row:  0 },
  'at': { col: 12, row:  0 },
  'au': { col: 13, row:  0 },
  'aw': { col: 14, row:  0 },
  'ax': { col: 15, row:  0 },
  'az': { col:  0, row:  1 },
  'ba': { col:  1, row:  1 },
  'bb': { col:  2, row:  1 },
  'bd': { col:  3, row:  1 },
  'be': { col:  4, row:  1 },
  'bf': { col:  5, row:  1 },
  'bg': { col:  6, row:  1 },
  'bh': { col:  7, row:  1 },
  'bi': { col:  8, row:  1 },
  'bj': { col:  9, row:  1 },
  'bl': { col: 10, row:  1 },
  'bm': { col: 11, row:  1 },
  'bn': { col: 12, row:  1 },
  'bo': { col: 13, row:  1 },
  'br': { col: 14, row:  1 },
  'bs': { col: 15, row:  1 },
  'bt': { col:  0, row:  2 },
  'bw': { col:  1, row:  2 },
  'by': { col:  2, row:  2 },
  'bz': { col:  3, row:  2 },
  'ca': { col:  4, row:  2 },
  'cd': { col:  5, row:  2 },
  'cf': { col:  6, row:  2 },
  'cg': { col:  7, row:  2 },
  'ch': { col:  8, row:  2 },
  'ci': { col:  9, row:  2 },
  'ck': { col: 10, row:  2 },
  'cl': { col: 11, row:  2 },
  'cm': { col: 12, row:  2 },
  'cn': { col: 13, row:  2 },
  'co': { col: 14, row:  2 },
  'cr': { col: 15, row:  2 },
  'cu': { col:  0, row:  3 },
  'cv': { col:  1, row:  3 },
  'cw': { col:  2, row:  3 },
  'cy': { col:  3, row:  3 },
  'cz': { col:  4, row:  3 },
  'de': { col:  5, row:  3 },
  'dj': { col:  6, row:  3 },
  'dk': { col:  7, row:  3 },
  'dm': { col:  8, row:  3 },
  'do': { col:  9, row:  3 },
  'dz': { col: 10, row:  3 },
  'ec': { col: 11, row:  3 },
  'ee': { col: 12, row:  3 },
  'eg': { col: 13, row:  3 },
  'eh': { col: 14, row:  3 },
  'en-us': { col: 14, row: 13 },
  'er': { col: 15, row:  3 },
  'es': { col:  0, row:  4 },
  'et': { col:  1, row:  4 },
  'eu': { col:  2, row:  4 },
  'fi': { col:  3, row:  4 },
  'fj': { col:  4, row:  4 },
  'fk': { col:  5, row:  4 },
  'fm': { col:  6, row:  4 },
  'fo': { col:  7, row:  4 },
  'fr': { col:  8, row:  4 },
  'ga': { col:  9, row:  4 },
  'gb': { col: 10, row:  4 },
  'gd': { col: 11, row:  4 },
  'ge': { col: 12, row:  4 },
  'gg': { col: 13, row:  4 },
  'gh': { col: 14, row:  4 },
  'gi': { col: 15, row:  4 },
  'gl': { col:  0, row:  5 },
  'gm': { col:  1, row:  5 },
  'gn': { col:  2, row:  5 },
  'gq': { col:  3, row:  5 },
  'gr': { col:  4, row:  5 },
  'gs': { col:  5, row:  5 },
  'gt': { col:  6, row:  5 },
  'gu': { col:  7, row:  5 },
  'gw': { col:  8, row:  5 },
  'gy': { col:  9, row:  5 },
  'hk': { col: 10, row:  5 },
  'hn': { col: 11, row:  5 },
  'hr': { col: 12, row:  5 },
  'ht': { col: 13, row:  5 },
  'hu': { col: 14, row:  5 },
  'ic': { col: 15, row:  5 },
  'id': { col:  0, row:  6 },
  'ie': { col:  1, row:  6 },
  'il': { col:  2, row:  6 },
  'im': { col:  3, row:  6 },
  'in': { col:  4, row:  6 },
  'iq': { col:  5, row:  6 },
  'ir': { col:  6, row:  6 },
  'is': { col:  7, row:  6 },
  'it': { col:  8, row:  6 },
  'je': { col:  9, row:  6 },
  'jm': { col: 10, row:  6 },
  'jo': { col: 11, row:  6 },
  'jp': { col: 12, row:  6 },
  'ke': { col: 13, row:  6 },
  'kg': { col: 14, row:  6 },
  'kh': { col: 15, row:  6 },
  'ki': { col:  0, row:  7 },
  'km': { col:  1, row:  7 },
  'kn': { col:  2, row:  7 },
  'kp': { col:  3, row:  7 },
  'kr': { col:  4, row:  7 },
  'kw': { col:  5, row:  7 },
  'ky': { col:  6, row:  7 },
  'kz': { col:  7, row:  7 },
  'la': { col:  8, row:  7 },
  'lb': { col:  9, row:  7 },
  'lc': { col: 10, row:  7 },
  'li': { col: 11, row:  7 },
  'lk': { col: 12, row:  7 },
  'lr': { col: 13, row:  7 },
  'ls': { col: 14, row:  7 },
  'lt': { col: 15, row:  7 },
  'lu': { col:  0, row:  8 },
  'lv': { col:  1, row:  8 },
  'ly': { col:  2, row:  8 },
  'ma': { col:  3, row:  8 },
  'mc': { col:  4, row:  8 },
  'md': { col:  5, row:  8 },
  'me': { col:  6, row:  8 },
  'mf': { col:  7, row:  8 },
  'mg': { col:  8, row:  8 },
  'mh': { col:  9, row:  8 },
  'mk': { col: 10, row:  8 },
  'ml': { col: 11, row:  8 },
  'mm': { col: 12, row:  8 },
  'mn': { col: 13, row:  8 },
  'mo': { col: 14, row:  8 },
  'mp': { col: 15, row:  8 },
  'mq': { col:  0, row:  9 },
  'mr': { col:  1, row:  9 },
  'ms': { col:  2, row:  9 },
  'mt': { col:  3, row:  9 },
  'mu': { col:  4, row:  9 },
  'mv': { col:  5, row:  9 },
  'mw': { col:  6, row:  9 },
  'mx': { col:  7, row:  9 },
  'my': { col:  8, row:  9 },
  'mz': { col:  9, row:  9 },
  'na': { col: 10, row:  9 },
  'nc': { col: 11, row:  9 },
  'ne': { col: 12, row:  9 },
  'nf': { col: 13, row:  9 },
  'ng': { col: 14, row:  9 },
  'ni': { col: 15, row:  9 },
  'nl': { col:  0, row: 10 },
  'no': { col:  1, row: 10 },
  'np': { col:  2, row: 10 },
  'nr': { col:  3, row: 10 },
  'nu': { col:  4, row: 10 },
  'nz': { col:  5, row: 10 },
  'om': { col:  6, row: 10 },
  'pa': { col:  7, row: 10 },
  'pe': { col:  8, row: 10 },
  'pf': { col:  9, row: 10 },
  'pg': { col: 10, row: 10 },
  'ph': { col: 11, row: 10 },
  'pk': { col: 12, row: 10 },
  'pl': { col: 13, row: 10 },
  'pn': { col: 14, row: 10 },
  'pr': { col: 15, row: 10 },
  'ps': { col:  0, row: 11 },
  'pt': { col:  1, row: 11 },
  'pt-br': { col: 14, row:  1 },
  'pw': { col:  2, row: 11 },
  'py': { col:  3, row: 11 },
  'qa': { col:  4, row: 11 },
  'ro': { col:  5, row: 11 },
  'rs': { col:  6, row: 11 },
  'ru': { col:  7, row: 11 },
  'rw': { col:  8, row: 11 },
  'sa': { col:  9, row: 11 },
  'sb': { col: 10, row: 11 },
  'sc': { col: 11, row: 11 },
  'sd': { col: 12, row: 11 },
  'se': { col: 13, row: 11 },
  'sg': { col: 14, row: 11 },
  'sh': { col: 15, row: 11 },
  'si': { col:  0, row: 12 },
  'sk': { col:  1, row: 12 },
  'sl': { col:  2, row: 12 },
  'sm': { col:  3, row: 12 },
  'sn': { col:  4, row: 12 },
  'so': { col:  5, row: 12 },
  'sr': { col:  6, row: 12 },
  'ss': { col:  7, row: 12 },
  'st': { col:  8, row: 12 },
  'sv': { col:  9, row: 12 },
  'sy': { col: 10, row: 12 },
  'sz': { col: 11, row: 12 },
  'tc': { col: 12, row: 12 },
  'td': { col: 13, row: 12 },
  'tf': { col: 14, row: 12 },
  'tg': { col: 15, row: 12 },
  'th': { col:  0, row: 13 },
  'tj': { col:  1, row: 13 },
  'tk': { col:  2, row: 13 },
  'tl': { col:  3, row: 13 },
  'tm': { col:  4, row: 13 },
  'tn': { col:  5, row: 13 },
  'to': { col:  6, row: 13 },
  'tr': { col:  7, row: 13 },
  'tt': { col:  8, row: 13 },
  'tv': { col:  9, row: 13 },
  'tw': { col: 10, row: 13 },
  'tz': { col: 11, row: 13 },
  'ua': { col: 12, row: 13 },
  'ug': { col: 13, row: 13 },
  'us': { col: 14, row: 13 },
  'uy': { col: 15, row: 13 },
  'uz': { col:  0, row: 14 },
  'va': { col:  1, row: 14 },
  'vc': { col:  2, row: 14 },
  've': { col:  3, row: 14 },
  'vg': { col:  4, row: 14 },
  'vi': { col:  5, row: 14 },
  'vn': { col:  6, row: 14 },
  'vu': { col:  7, row: 14 },
  'wf': { col:  8, row: 14 },
  'ws': { col:  9, row: 14 },
  'ye': { col: 10, row: 14 },
  'yt': { col: 11, row: 14 },
  'za': { col: 12, row: 14 },
  'zm': { col: 13, row: 14 },
  'zw': { col: 14, row: 14 },
};

/**
 * ISO 3166-1 alpha-3 → alpha-2 lookup table (245 entries).
 * Used by getFlagPosition to resolve country codes from the backend
 * CountryDto.code field (which uses alpha-3) to the alpha-2 keys
 * used in FLAG_POSITIONS.
 */
export const ISO3_TO_ALPHA2: Record<string, string> = {
  'ABW': 'aw', 'AFG': 'af', 'AGO': 'ao', 'AIA': 'ai', 'ALA': 'ax',
  'ALB': 'al', 'AND': 'ad', 'ARE': 'ae', 'ARG': 'ar', 'ARM': 'am',
  'ASM': 'as', 'ATF': 'tf', 'ATG': 'ag', 'AUS': 'au', 'AUT': 'at',
  'AZE': 'az', 'BDI': 'bi', 'BEL': 'be', 'BEN': 'bj', 'BES': 'bq',
  'BFA': 'bf', 'BGD': 'bd', 'BGR': 'bg', 'BHR': 'bh', 'BHS': 'bs',
  'BIH': 'ba', 'BLM': 'bl', 'BLR': 'by', 'BLZ': 'bz', 'BMU': 'bm',
  'BOL': 'bo', 'BRA': 'br', 'BRB': 'bb', 'BRN': 'bn', 'BTN': 'bt',
  'BVT': 'bv', 'BWA': 'bw', 'CAF': 'cf', 'CAN': 'ca', 'CCK': 'cc',
  'CHE': 'ch', 'CHL': 'cl', 'CHN': 'cn', 'CIV': 'ci', 'CMR': 'cm',
  'COD': 'cd', 'COG': 'cg', 'COK': 'ck', 'COL': 'co', 'COM': 'km',
  'CPV': 'cv', 'CRI': 'cr', 'CUB': 'cu', 'CUW': 'cw', 'CXR': 'cx',
  'CYM': 'ky', 'CYP': 'cy', 'CZE': 'cz', 'DEU': 'de', 'DJI': 'dj',
  'DMA': 'dm', 'DNK': 'dk', 'DOM': 'do', 'DZA': 'dz', 'ECU': 'ec',
  'EGY': 'eg', 'ERI': 'er', 'ESH': 'eh', 'ESP': 'es', 'EST': 'ee',
  'ETH': 'et', 'FIN': 'fi', 'FJI': 'fj', 'FLK': 'fk', 'FRA': 'fr',
  'FRO': 'fo', 'FSM': 'fm', 'GAB': 'ga', 'GBR': 'gb', 'GEO': 'ge',
  'GGY': 'gg', 'GHA': 'gh', 'GIB': 'gi', 'GIN': 'gn', 'GLP': 'gp',
  'GMB': 'gm', 'GNB': 'gw', 'GNQ': 'gq', 'GRC': 'gr', 'GRD': 'gd',
  'GRL': 'gl', 'GTM': 'gt', 'GUF': 'gf', 'GUM': 'gu', 'GUY': 'gy',
  'HKG': 'hk', 'HND': 'hn', 'HRV': 'hr', 'HTI': 'ht', 'HUN': 'hu',
  'IDN': 'id', 'IMN': 'im', 'IND': 'in', 'IOT': 'io', 'IRL': 'ie',
  'IRN': 'ir', 'IRQ': 'iq', 'ISL': 'is', 'ISR': 'il', 'ITA': 'it',
  'JAM': 'jm', 'JEY': 'je', 'JOR': 'jo', 'JPN': 'jp', 'KAZ': 'kz',
  'KEN': 'ke', 'KGZ': 'kg', 'KHM': 'kh', 'KIR': 'ki', 'KNA': 'kn',
  'KOR': 'kr', 'KWT': 'kw', 'LAO': 'la', 'LBN': 'lb', 'LBR': 'lr',
  'LBY': 'ly', 'LCA': 'lc', 'LIE': 'li', 'LKA': 'lk', 'LSO': 'ls',
  'LTU': 'lt', 'LUX': 'lu', 'LVA': 'lv', 'MAC': 'mo', 'MAF': 'mf',
  'MAR': 'ma', 'MCO': 'mc', 'MDA': 'md', 'MDG': 'mg', 'MDV': 'mv',
  'MEX': 'mx', 'MHL': 'mh', 'MKD': 'mk', 'MLI': 'ml', 'MLT': 'mt',
  'MMR': 'mm', 'MNE': 'me', 'MNG': 'mn', 'MNP': 'mp', 'MOZ': 'mz',
  'MRT': 'mr', 'MSR': 'ms', 'MTQ': 'mq', 'MUS': 'mu', 'MWI': 'mw',
  'MYS': 'my', 'MYT': 'yt', 'NAM': 'na', 'NCL': 'nc', 'NER': 'ne',
  'NFK': 'nf', 'NGA': 'ng', 'NIC': 'ni', 'NIU': 'nu', 'NLD': 'nl',
  'NOR': 'no', 'NPL': 'np', 'NRU': 'nr', 'NZL': 'nz', 'OMN': 'om',
  'PAK': 'pk', 'PAN': 'pa', 'PCN': 'pn', 'PER': 'pe', 'PHL': 'ph',
  'PLW': 'pw', 'PNG': 'pg', 'POL': 'pl', 'PRI': 'pr', 'PRK': 'kp',
  'PRT': 'pt', 'PRY': 'py', 'PSE': 'ps', 'PYF': 'pf', 'QAT': 'qa',
  'REU': 're', 'ROU': 'ro', 'RUS': 'ru', 'RWA': 'rw', 'SAU': 'sa',
  'SDN': 'sd', 'SEN': 'sn', 'SGP': 'sg', 'SGS': 'gs', 'SHN': 'sh',
  'SJM': 'sj', 'SLB': 'sb', 'SLE': 'sl', 'SLV': 'sv', 'SMR': 'sm',
  'SOM': 'so', 'SPM': 'pm', 'SRB': 'rs', 'SSD': 'ss', 'STP': 'st',
  'SUR': 'sr', 'SVK': 'sk', 'SVN': 'si', 'SWE': 'se', 'SWZ': 'sz',
  'SXM': 'sx', 'SYC': 'sc', 'SYR': 'sy', 'TCA': 'tc', 'TCD': 'td',
  'TGO': 'tg', 'THA': 'th', 'TJK': 'tj', 'TKL': 'tk', 'TKM': 'tm',
  'TLS': 'tl', 'TON': 'to', 'TTO': 'tt', 'TUN': 'tn', 'TUR': 'tr',
  'TUV': 'tv', 'TWN': 'tw', 'TZA': 'tz', 'UGA': 'ug', 'UKR': 'ua',
  'UMI': 'um', 'URY': 'uy', 'USA': 'us', 'UZB': 'uz', 'VAT': 'va',
  'VCT': 'vc', 'VEN': 've', 'VGB': 'vg', 'VIR': 'vi', 'VNM': 'vn',
  'VUT': 'vu', 'WLF': 'wf', 'WSM': 'ws', 'YEM': 'ye', 'ZAF': 'za',
  'ZMB': 'zm', 'ZWE': 'zw',
};

/**
 * Fallback map for territories whose flags are not in the sprite.
 * Each entry maps an alpha-2 code to the administering country's alpha-2 code.
 */
const FALLBACK_FLAGS: Record<string, string> = {
  're': 'fr',  // Réunion — French overseas dept
  'gp': 'fr',  // Guadeloupe — French overseas dept
  'gf': 'fr',  // French Guiana — French overseas territory
  'pm': 'fr',  // Saint Pierre and Miquelon — French collectivity
  'cc': 'au',  // Cocos (Keeling) Islands — Australian territory
  'cx': 'au',  // Christmas Island — Australian territory
  'sj': 'no',  // Svalbard and Jan Mayen — Norwegian territory
  'bv': 'no',  // Bouvet Island — uninhabited Norwegian territory
  'io': 'gb',  // British Indian Ocean Territory — British territory
  'sx': 'nl',  // Sint Maarten (Dutch part) — Kingdom of Netherlands
  'bq': 'nl',  // Bonaire, Sint Eustatius and Saba — Kingdom of Netherlands
  'um': 'us',  // U.S. Minor Outlying Islands — U.S. territories
};

/**
 * Resolve a BCP-47 locale, alpha-2, or alpha-3 code to a sprite position.
 *
 * Lookup order:
 *   1. Direct key match ('en-US', 'pt-BR')
 *   2. Lowercased key ('en-us', 'pt-br')
 *   3. ISO alpha-3 → alpha-2 conversion ('KEN' → 'ke')
 *   4. Region suffix only ('us' from 'en-US')
 *   5. Language prefix only ('en' from 'en-US')
 *   6. Territory fallback — administering country flag
 *   7. `null` -> Flag component renders neutral fallback tile
 */
export function getFlagPosition(locale: string): FlagPosition | null {
  if (FLAG_POSITIONS[locale]) return FLAG_POSITIONS[locale]!;
  const lower = locale.toLowerCase();
  if (FLAG_POSITIONS[lower]) return FLAG_POSITIONS[lower]!;
  const alpha2 = ISO3_TO_ALPHA2[locale.toUpperCase()] ?? lower;
  if (FLAG_POSITIONS[alpha2]) return FLAG_POSITIONS[alpha2]!;
  const parts = lower.split('-');
  if (parts.length > 1) {
    const region = parts[parts.length - 1]!;
    if (FLAG_POSITIONS[region]) return FLAG_POSITIONS[region]!;
  }
  const lang = parts[0]!;
  if (FLAG_POSITIONS[lang]) return FLAG_POSITIONS[lang]!;
  const fallback = FALLBACK_FLAGS[alpha2];
  if (fallback && FLAG_POSITIONS[fallback]) return FLAG_POSITIONS[fallback]!;
  return null;
}
